/**
 * Единый контент инструкции «Умный импорт» (страница addprovider.ru/docs и
 * разделы приложения «Вопрос-Ответ» / «Обновления»).
 *
 * ⭐ ПРАВКИ СПРАВКИ — ТОЛЬКО ЗДЕСЬ. Отсюда 1:1 идёт и на сайт, и в приложение.
 * При добавлении раздела — обновить helpTableOfContents; при новой возможности —
 * запись в helpChangelog (новое сверху, с датой). Синхронно обновить ZIP-инструкцию
 * docs/ms-instruction/index.html. Только структура и текст; стили — в DocsPage.
 *
 * Стиль текста: для НЕтехнических пользователей (умеют Word/Excel/интернет).
 * Простым языком, по шагам, с примерами, подписи кнопок/полей — как в приложении.
 */
import type { ReactNode } from 'react';

export interface HelpSection {
  id: string;
  title: string;
  content: ReactNode;
}
export interface HelpFaqItem {
  question: string;
  answer: ReactNode;
}
export interface HelpChangelogEntry {
  date?: string;
  title: string;
  body: ReactNode;
}

export const supportEmail = 'support@e-ion.ru';
/** Чат поддержки в Telegram — быстрый ответ. */
export const supportTelegram = 'https://t.me/sup_eion';

export const helpTableOfContents: Array<Pick<HelpSection, 'id' | 'title'>> = [
  { id: 'overview', title: 'Что это и зачем' },
  { id: 'install', title: 'Шаг 1. Установка' },
  { id: 'first-rule', title: 'Шаг 2. Первый поставщик' },
  { id: 'preview-run', title: 'Шаг 3. Предпросмотр и загрузка' },
  { id: 'results', title: 'Шаг 4. Отчёт' },
  { id: 'matching', title: 'Как находятся ваши товары' },
  { id: 'not-found', title: 'Если много «не найдено»' },
  { id: 'price', title: 'Цены и формулы' },
  { id: 'stock', title: 'Остатки, склады, коробки' },
  { id: 'filters', title: 'Что пропускается само' },
  { id: 'exclusions', title: 'Исключить товары и файлы' },
  { id: 'repeat', title: 'Повторная загрузка' },
  { id: 'draft', title: 'Черновик или сразу провести' },
  { id: 'supplier-card', title: 'Поставщик в карточке' },
  { id: 'auto', title: 'Автоматизация: почта, ссылка, облако, YML и XML' },
  { id: 'mail', title: 'Настройка почты' },
  { id: 'schedule', title: 'Расписание' },
  { id: 'multi', title: 'Штучно и коробками' },
  { id: 'suppliers-list', title: 'Список поставщиков и группы' },
  { id: 'reports', title: 'Отчёты' },
  { id: 'prices-compare', title: 'Сравнение цен' },
  { id: 'stats', title: 'Статистика' },
  { id: 'backup', title: 'Бэкап и перенос' },
  { id: 'trial', title: 'Пробный период и поддержка' },
  { id: 'faq', title: 'Вопрос-Ответ' },
  { id: 'changelog', title: 'История обновлений' },
];

export const helpSections: HelpSection[] = [
  {
    id: 'overview',
    title: 'Что это и зачем',
    content: (
      <>
        <p>
          <b>Умный импорт</b> — приложение внутри МойСклад. Поставщики присылают вам прайс‑листы
          (файлы Excel/CSV со списком товаров, ценами и остатками), а вы обычно вручную переносите это
          в МойСклад. Приложение делает это за вас: берёт прайс, <b>находит ваши товары</b> в каталоге,
          создаёт документ <b>«Оприходование»</b>, обновляет <b>закупочную цену</b> (по желанию — и цену
          продажи) и <b>остатки</b>.
        </p>
        <p>
          <b>«Оприходование»</b> — это документ МойСклад, которым товар ставится на склад (приход).
          Раньше вы завели бы его руками — теперь приложение создаёт такой документ за вас, а вы можете
          сначала посмотреть, что получится. Слова «приход» и «создать приходы» ниже означают то же самое.
        </p>
        <p>
          Отдельный сайт не нужен — всё работает прямо в вашем МойСклад. Прайс можно загружать вручную
          файлом или настроить, чтобы приложение забирало его само — из почты, по ссылке, из облачной
          папки или YML‑каталога — и обрабатывало по расписанию.
        </p>
        <p>
          Ниже — пошаговая инструкция: от установки до первой успешной загрузки, а затем — все тонкости
          настройки. Настраивать нужно один раз на каждого поставщика; дальше — по кнопке или само.
          Не понадобится ни строчки кода: всё мышкой, по образцу вашего файла.
        </p>
      </>
    ),
  },
  {
    id: 'install',
    title: 'Шаг 1. Установка и первый вход',
    content: (
      <>
        <ul>
          <li>В МойСклад откройте раздел <b>«Приложения»</b> (магазин приложений), найдите
            <b> «Умный импорт»</b> и нажмите <b>«Установить»</b>. Согласитесь с доступом — он нужен,
            чтобы находить ваши товары и создавать оприходования.</li>
          <li>После установки откройте приложение — оно встроено прямо в МойСклад, отдельно ничего
            запускать не нужно.</li>
          <li>Первый экран — вкладка <b>«Поставщики»</b>. Она пока пустая: здесь будут ваши правила
            (по одному на каждого поставщика).</li>
        </ul>
        <p>
          Вверху есть вкладки: <b>Поставщики</b>, <b>Загрузка</b>, <b>Цены</b>, <b>Статистика</b>,
          <b> Отчёты</b>, <b>Почта</b>, <b>Обновления</b>, <b>Вопрос‑Ответ</b>. Начните с «Поставщики»
          — создадим первое правило.
        </p>
        <p>
          Что понадобится: аккаунт МойСклад, где уже заведены ваши товары (с артикулами или кодами),
          и прайс от поставщика (файл Excel/CSV). Если товаров в МойСклад ещё нет — приложение их
          не найдёт, но подскажет в отчёте, каких не хватает.
        </p>
      </>
    ),
  },
  {
    id: 'first-rule',
    title: 'Шаг 2. Настраиваем первого поставщика',
    content: (
      <>
        <p>
          На вкладке <b>«Поставщики»</b> нажмите <b>«Новый поставщик»</b>. Откроется форма настройки —
          пройдём её сверху вниз. Совет: заранее сохраните на компьютер один прайс от этого поставщика
          (тот самый файл Excel, что он вам присылает) — он понадобится на первом же шаге.
        </p>
        <ul>
          <li>
            <b>Подгрузите образец файла.</b> Нажмите <b>«Подгрузить поля из файла»</b> и выберите тот
            сохранённый прайс. Приложение прочитает названия колонок — дальше вы будете
            <b> выбирать колонки из списка</b>, а не вписывать их вручную. Это самый простой путь.
          </li>
          <li>
            <b>Название поставщика.</b> Впишите любое понятное имя, например «Вина Испании» — так
            правило будет называться в списке.
          </li>
          <li>
            <b>Источник данных.</b> Для начала оставьте <b>«Файл (загрузка/почта)»</b> и способ
            <b> «Ручная загрузка»</b> — будете загружать файл сами (автоматику из почты/ссылки настроим
            позже, см. раздел «Автоматизация»).
          </li>
          <li>
            <b>Как узнавать файл.</b> Впишите часть имени файла этого поставщика (например,
            <code> ПРАЙС_ВИНА</code>). По ней приложение поймёт, что файл — именно от этого поставщика.
            Способ совпадения оставьте <b>«содержит»</b>.
          </li>
          <li>
            <b>Контрагент (поставщик).</b> Начните вводить название — идёт поиск по вашему справочнику
            МойСклад. Это тот, чей товар: подставится в оприходование и в поле «Поставщик» карточки.
          </li>
          <li>
            <b>Организация</b> и <b>Склад (куда приходуем).</b> Выберите из списка: на какую
            организацию оформляем документ и на какой склад попадут остатки.
          </li>
          <li>
            <b>Сопоставление товара.</b> В <b>«Колонка в файле (откуда брать)»</b> выберите колонку с
            артикулом или кодом. В <b>«Искать в МойСклад по»</b> укажите, с каким полем карточки
            сравнивать — <b>Артикул</b> или <b>Код</b>. Важно, чтобы это поле было заполнено у ваших
            товаров.
          </li>
          <li>
            <b>Себестоимость (закупочная цена).</b> Оставьте <b>«Из колонки прайса»</b> и выберите
            <b> колонку закупочной цены</b>. Формулу и цену продажи пока не трогайте — вернёмся к ним
            в разделе «Цены».
          </li>
          <li>
            <b>Остатки.</b> В <b>«Колонки остатков»</b> выберите колонку с остатком. Если складов
            несколько — можно добавить ещё колонки, они сложатся в один остаток.
          </li>
        </ul>
        <p>
          Нажмите <b>«Сохранить»</b> (правило можно сохранить, не выходя из формы). Всё — базовое
          правило готово. Остальные поля (очистка артикула, наценки, исключения, расписание) —
          необязательные, их добавите, когда понадобится.
        </p>
      </>
    ),
  },
  {
    id: 'preview-run',
    title: 'Шаг 3. Предпросмотр и создание приходов',
    content: (
      <>
        <p>
          Перед тем как что‑то менять в МойСклад, всегда есть <b>предпросмотр</b> — он ничего не
          создаёт, только показывает, что получится.
        </p>
        <ul>
          <li>
            <b>Предпросмотр.</b> В правиле (или на вкладке <b>«Загрузка»</b>) выберите файл и нажмите
            <b> «Предпросмотр»</b>. Приложение обработает файл и покажет: <b>сколько будет загружено</b>,
            <b> сколько не найдено</b> и <b>сколько пропущено</b>, плюс список ненайденных. Документы
            при этом НЕ создаются.
          </li>
          <li>
            <b>Проверьте цифры.</b> Если «загружено» близко к числу строк в файле — хорошо. Если много
            «не найдено» — см. раздел «Если много «не найдено»» ниже, поправьте правило и повторите
            предпросмотр.
          </li>
          <li>
            <b>Создать приходы.</b> Когда всё устраивает — нажмите <b>«Создать приходы»</b>. Приложение
            создаст оприходование. Большие прайсы обрабатываются в фоне: приложение сразу примет
            задачу, а результат появится во вкладке <b>«Отчёты»</b>.
          </li>
        </ul>
        <p>
          Загружаете несколько файлов сразу? Перейдите на вкладку <b>«Загрузка»</b>, перетащите туда
          файлы (можно несколько), посмотрите предпросмотр по каждому и нажмите <b>«Создать
          оприходования»</b> — все уйдут в обработку.
        </p>
        <p>
          Если приложение написало <b>«правило не найдено»</b> — значит имя файла не совпало ни с одним
          правилом (при ручной загрузке это нормально). Под файлом появится список <b>«Выберите правило
          для этого файла вручную»</b> — укажите нужное правило и повторите предпросмотр. Чтобы такие
          файлы определялись сами, впишите в правиле часть имени файла в поле «Как узнавать файл» (для
          ручной загрузки оно необязательное).
        </p>
        <p>
          Не пугайтесь разных подписей: <b>«Создать приходы»</b> (в карточке правила) и <b>«Создать
          оприходования»</b> (на вкладке «Загрузка») — это одно и то же действие. Так же и с загрузкой
          образца: <b>«Подгрузить поля из файла»</b> в правиле и <b>«Загрузить поля из файла»</b> для
          источника‑ссылки делают одно — читают названия колонок из вашего файла.
        </p>
      </>
    ),
  },
  {
    id: 'results',
    title: 'Шаг 4. Смотрим отчёт',
    content: (
      <>
        <p>
          Вкладка <b>«Отчёты»</b> — список всех загрузок. Пока идёт обработка, статус
          <b> «В обработке»</b> сам сменится на <b>«Готово»</b> (список обновляется автоматически).
          Нажмите на строку — откроется детальный отчёт.
        </p>
        <ul>
          <li><b>Загружено</b> — товары найдены, цена и остаток записаны.</li>
          <li><b>Не найдено</b> — товара с таким артикулом/кодом нет в вашем МойСклад.</li>
          <li><b>Пропущено</b> — строка без ключа, без цены или с нулевым остатком (это нормально).</li>
          <li>Также видно, сколько карточек обновлено по <b>закупке</b>, <b>цене продажи</b> и
            <b> поставщику</b>.</li>
        </ul>
        <p>
          Кнопка <b>«Скачать не найденные в Excel»</b> выгрузит список товаров, которых у вас нет —
          удобно, чтобы завести их карточки в МойСклад, а потом загрузить прайс снова. Внизу отчёта —
          «Проблемные строки» (с причиной по каждой) и «Загруженные позиции».
        </p>
      </>
    ),
  },
  {
    id: 'matching',
    title: 'Как приложение находит ваши товары',
    content: (
      <>
        <p>
          Товар из прайса ищется в МойСклад по <b>Артикулу</b> или <b>Коду</b> (что выбрали в правиле).
          Значение из колонки файла сравнивается с этим полем карточки. Часто артикулы у поставщика
          записаны чуть иначе — значение можно <b>подготовить перед поиском</b> (всё необязательно):
        </p>
        <ul>
          <li><b>Добавить перед / после</b> — префикс/суффикс. Пример: файл <code>6449</code> +
            префикс <code>BG</code> = ищем <code>BG6449</code>.</li>
          <li><b>Убрать в начале / в конце</b> — отрезать лишнее (например, начинается с
            <code> ART-</code> или заканчивается на <code>-RU</code>).</li>
          <li><b>Убрать подстроку</b> — вырезать кусок везде, где встречается (например, <code>0000</code>).</li>
          <li><b>Убрать символы</b> — удалить каждый символ из набора (например, пробел или
            <code> /</code>: <code>120 586</code> → <code>120586</code>).</li>
        </ul>
        <p>
          Порядок применения: сначала очистка (в начале → в конце → подстрока → символы), затем
          добавляются префикс/суффикс. В предпросмотре сразу видно, сколько товаров нашлось —
          подбирайте, пока «не найдено» не станет минимальным.
        </p>
        <p>
          <b>Поиск по штрихкоду.</b> Если поставщик присылает в прайсе штрихкоды, выберите в «Искать
          в МойСклад по» вариант <b>«Штрихкоду»</b>. Сравнение идёт со <b>штатным полем «Штрихкоды»</b>
          карточки товара — тем, что заполняется в МойСклад в разделе «Штрихкоды» (EAN-13, EAN-8,
          Code128, GTIN, UPC). Если у товара несколько штрихкодов, он найдётся по <b>любому</b> из
          них. Удобно, когда артикулы у вас с поставщиком не совпадают, а штрихкод — общий язык.
          Учтите: если вы завели <b>своё доп. поле</b> со штрихкодом, оно сюда не относится — для
          него выбирайте «Текстовому доп. полю».
        </p>
        <p>
          <b>Поиск по текстовому доп. полю (для разных артикулов у поставщиков).</b> Бывает, что один
          и тот же ваш товар у разных поставщиков называется разными артикулами. Тогда в «Искать в
          МойСклад по» выберите <b>«Текстовому доп. полю»</b>. Идея: в карточке товара заведите доп.
          поля (например «Артикул поставщика А», «Артикул поставщика Б»), впишите туда артикул каждого
          поставщика, а в правиле этого поставщика матчите по его полю. Один ваш товар — сколько угодно
          «поставщицких» артикулов. Важно: доп. поле должно быть типа <b>«Текст»</b>, а значения в него
          вносятся в МойСклад вручную по каждому товару (приложение их не заполняет).
        </p>
      </>
    ),
  },
  {
    id: 'not-found',
    title: 'Если много «не найдено»',
    content: (
      <>
        <p>Это самая частая ситуация в начале. Разбираемся по шагам:</p>
        <ul>
          <li>
            <b>Скачайте не найденные</b> (кнопка в отчёте). В файле две колонки: «Артикул из прайса»
            (что было) и «Искали в МойСклад» (что искали после подготовки). Откройте пару таких
            товаров в МойСклад и сравните с их реальным артикулом/кодом.
          </li>
          <li>
            <b>Не то поле поиска.</b> Если у ваших товаров заполнен <b>Код</b>, а не Артикул (или
            наоборот) — переключите «Искать в МойСклад по».
          </li>
          <li>
            <b>Артикул чуть отличается.</b> Например, в прайсе <code>ART-6449</code>, а у вас
            <code> 6449</code> — уберите префикс <code>ART-</code>. Или наоборот, добавьте недостающий
            префикс. Подбирайте в предпросмотре.
          </li>
          <li>
            <b>Товаров правда нет в МойСклад.</b> Тогда приложение их и не найдёт — это защита от
            засорения каталога. Заведите карточки (можно по скачанному списку) и загрузите прайс снова.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'price',
    title: 'Цены: закупка, продажа, формулы',
    content: (
      <>
        <p>
          <b>Импорт себестоимости — по желанию.</b> Вверху блока «Себестоимость» есть переключатель
          <b> «Импортировать себестоимость (закупочную цену) из прайса»</b> (по умолчанию включён).
          Если он <b>выключен</b> — приложение обновит <b>только остатки</b>, а оприходование пройдёт по
          <b> текущей закупочной цене карточки</b>: себестоимость в МойСклад <b>останется как есть</b>,
          ничего не изменится. Удобно, когда от поставщика нужны только остатки.
        </p>
        <p>
          Если импорт включён — <b>себестоимость (закупочная)</b> берётся из колонки прайса или считается
          из другой цены. К ней можно применить <b>формулу</b> — цепочку операций слева направо:
        </p>
        <ul>
          <li><b>× умножить</b> / <b>÷ разделить</b> на число;</li>
          <li><b>+ / − процент</b> (наценка/скидка): например, <code>− 40%</code> от розницы даёт
            себестоимость;</li>
          <li><b>+ / − рубли</b>;</li>
          <li><b>× / ÷ на кол‑во в коробке</b> (см. раздел про коробки).</li>
        </ul>
        <p>
          <b>Округление:</b> до копейки (по умолчанию), до рубля или без округления. При включённом
          импорте закупочная цена записывается в карточку.
        </p>
        <p>
          <b>Цена продажи</b> — по желанию. Включите <b>«Обновлять цену продажи в карточке товара»</b>,
          выберите, откуда её брать (из колонки прайса или <b>рассчитать из себестоимости</b>, например
          <code> × 1.5</code>), задайте формулу и <b>тип цены</b> МойСклад. Тип цены — это куда в
          карточке записать цену продажи (в МойСклад их бывает несколько: например, «Цена продажи»,
          «Розничная», «Оптовая»); выберите из списка тот, что используете при продаже. Если считаете
          и то, и другое — сначала вычисляется себестоимость, потом от неё цена продажи.
        </p>
        <p>
          Приложение не даст записать «плохую» цену: если формула даёт 0, минус или ошибку (например,
          деление на ноль) — строка пропускается и попадает в отчёт, а не портит карточку.
        </p>
      </>
    ),
  },
  {
    id: 'stock',
    title: 'Остатки: колонки, склады, коробки',
    content: (
      <>
        <p>
          В <b>«Колонки остатков»</b> укажите одну или несколько колонок — <b>несколько складов
          суммируются</b> в общий остаток. К результату можно применить <b>формулу остатка</b> и
          <b> округление</b> (до целого — по умолчанию, вверх, вниз или без).
        </p>
        <p>
          <b>Нужно обновить только цены?</b> Включите переключатель <b>«Не учитывать наличие»</b>.
          Тогда приложение не читает и не меняет остатки, не создаёт оприходование и обновляет только
          те цены, которые вы включили в разделах «Себестоимость» и «Цена продажи».
        </p>
        <p>
          <b>Коробки → штуки.</b> Если поставщик даёт остаток в коробках, а вы продаёте поштучно:
          заполните поле <b>«Колонка «кол‑во в коробке»»</b> (приложение вытащит число из текста:
          <code> КОР6</code> → 6, <code>12 шт</code> → 12), а в формуле остатка добавьте
          <b> «× на кол‑во в коробке»</b>. Тогда 5 коробок × 12 = 60 штук.
        </p>
        <p>
          <b>Правила остатка (необязательно).</b> Бывает, что в колонке остатка не число:
          поставщик пишет <b>словами</b> («много», «мало», «под заказ») или <b>срок поставки</b>
          («10 дней», «3 дня»). Тогда добавьте правило: условие (содержит / равно / начинается с /
          заканчивается на) и что записать. Например «содержит <code>дней</code> → 5» или
          «равно <code>много</code> → 50». Это же место решает обратную задачу — <b>ограничить
          остаток</b> числовым условием: «больше 100 → записать 10», чтобы не показывать реальную
          глубину склада поставщика, и «меньше 20 → записать 3», чтобы не продать то, что
          вот‑вот закончится. Правила проверяются сверху вниз, срабатывает первое подходящее;
          вместо числа можно выбрать <b>как есть</b> (оставить значение из файла) или
          <b>пропустить</b> (не приходовать товар).
        </p>
        <p>
          ⚠️ Без такого правила текст вроде «10 дней» приложение прочитает как число <b>10</b> —
          срок поставки попадёт в остаток. Если поставщик пишет сроки, обязательно добавьте правило.
        </p>
        <p>
          «Двухэтажные» и слитые шапки приложение распознаёт само: если ячейка заголовка пустая, берёт
          значение из строки выше. Значения вида <code>&gt;50</code>, <code>от 100</code> в колонках
          цены и остатка приводятся к числу автоматически.
        </p>
      </>
    ),
  },
  {
    id: 'filters',
    title: 'Что пропускается автоматически',
    content: (
      <>
        <p>Без всякой настройки приложение пропускает строки, которые нельзя оприходовать (всё видно в отчёте):</p>
        <ul>
          <li><b>Нет артикула/кода</b> — в колонке‑ключе пусто, искать нечего.</li>
          <li><b>Нет цены</b> — колонка себестоимости пустая или не число (а также если формула дала
            минус/ноль/ошибку).</li>
          <li><b>Нулевой остаток</b> — итоговый остаток 0 или меньше нуля (товар не приходуется).</li>
        </ul>
        <p>Это защита: в МойСклад не уйдут «кривые» строки, а вы увидите причину по каждой.</p>
      </>
    ),
  },
  {
    id: 'exclusions',
    title: 'Исключить часть товаров или файлов',
    content: (
      <>
        <p>
          Исключения бывают <b>двух видов</b>, и это разные вещи: одни смотрят в <b>файл
          поставщика</b>, другие — в <b>вашу карточку товара в МойСклад</b>.
        </p>
        <p>
          <b>Исключения по файлу поставщика.</b> Не приходовать позиции по условию: выберите <b>колонку</b>, тип
          совпадения (<b>содержит</b> / маска / регулярное выражение) и <b>значение</b>. Например,
          колонка «Категория» содержит <code>SALE</code> — акционные не пойдут. Несколько правил
          работают по <b>«ИЛИ»</b> (достаточно одного совпадения), регистр не важен. Исключённые видны
          в отчёте.
        </p>
        <p>
          Новичку достаточно варианта <b>«содержит»</b> — просто впишите слово (например, <code>SALE</code>).
          «Маска» и «регулярное выражение» — для сложных случаев, они не обязательны, можно спокойно их
          не трогать.
        </p>
        <p>
          <b>Исключения по каталогу МойСклад.</b> Здесь файл поставщика не читается вовсе.
          Приложение берёт товар из прайса, находит его <b>у вас в каталоге</b> и смотрит
          <b> карточку</b>: поставщика, бренд, флажок, название. Если карточка подходит под условие, товар
          <b> полностью пропускается</b> — не приходуется, не меняется закупочная цена, цена
          продажи и поставщик в карточке.
        </p>
        <p>
          Зачем это нужно. Один и тот же артикул часто есть у нескольких поставщиков. Скажем,
          товар бренда <code>Pulltex</code> вы заводите сами, а он попадается и в прайсе другого
          поставщика — и тот перезаписывает вашу закупочную цену и поставщика в карточке. Условие
          <b> «Бренд равно Pulltex»</b> в правиле <i>чужого</i> поставщика оставит такие товары
          нетронутыми.
        </p>
        <p>
          <b>По поставщику.</b> Условие «Поставщик — это …» удобно, когда товар уже закреплён за
          «своим» поставщиком: чужой прайс его не тронет. Контрагент выбирается поиском по вашему
          справочнику, а не вводится текстом — сравнение идёт по самому контрагенту, поэтому
          разное написание названия («ООО Ромашка» / «Ромашка ООО») не помешает. Есть и обратный
          вариант — «Поставщик — любой, кроме …».
        </p>
        <p>
          Поле выбирается <b>из списка</b> ваших доп. полей МойСклад — вводить название руками не
          нужно (у полей бывают похожие имена, легко ошибиться). Для полей-флажков предлагаются
          условия «отмечен» / «не отмечен», для текстовых — «равно», «содержит», «не содержит» и
          другие. У отрицательных условий есть отдельная галочка: считать ли подходящими товары,
          у которых поле <b>не заполнено</b>. По умолчанию она выключена — иначе одно условие
          исключило бы все товары без проставленного бренда.
        </p>
        <p>
          <b>Исключить файлы со словами.</b> Для источников «папка»/«файл»: файлы, в имени которых есть
          указанные слова (например, <code>SALE</code>, <code>архив</code>), пропускаются — удобно,
          когда в папке лежат и дневной, и акционный прайс.
        </p>
      </>
    ),
  },
  {
    id: 'repeat',
    title: 'Повторная загрузка того же поставщика',
    content: (
      <>
        <p>
          Загружаете прайс каждый день — остатки не задвоятся. Новый приход создаётся на текущую дату,
          а предыдущий приход этого правила <b>снимается с проведения</b> (перестаёт влиять на остатки)
          или <b>удаляется</b> — что именно, выбираете в правиле (поле «Предыдущее оприходование
          поставщика»).
        </p>
        <p>
          Приложение трогает <b>только свои</b> документы (по своей метке). Оприходования, которые вы
          завели вручную, оно не снимает и не удаляет.
        </p>
      </>
    ),
  },
  {
    id: 'draft',
    title: 'Черновик или сразу провести',
    content: (
      <>
        <p>В правиле есть выбор, что делать с оприходованием:</p>
        <ul>
          <li><b>Провести сразу</b> (по умолчанию) — документ проведён, остатки меняются сразу.</li>
          <li><b>Черновик</b> — документ создаётся непроведённым и <b>не влияет на остатки</b>, пока
            вы не проверите и не проведёте его в МойСклад вручную.</li>
        </ul>
        <p>
          Пока привыкаете — режим «Черновик» удобен: приложение всё посчитает, а вы спокойно
          проверите документ перед тем, как он повлияет на учёт. Если остатки «не изменились» — скорее
          всего документ остался черновиком, проведите его в МойСклад.
        </p>
      </>
    ),
  },
  {
    id: 'supplier-card',
    title: 'Поставщик в карточке товара',
    content: (
      <>
        <p>
          При импорте поле <b>«Поставщик»</b> в карточке найденного товара ставится равным контрагенту
          правила — тому, чья цена только что записана. Так поставщик и его цена в карточке всегда
          согласованы. Если поставщик уже верный, история товара не засоряется. Поле есть только у
          товаров (у модификаций/комплектов/услуг своего поставщика нет — их пропускаем).
        </p>
        <p>
          Управляется ползунком <b>«Обновлять поставщика в карточке товара»</b> (по умолчанию включён).
          Выключите, если по этому поставщику поле «Поставщик» трогать не нужно.
        </p>
      </>
    ),
  },
  {
    id: 'auto',
    title: 'Автоматизация: почта, ссылка, облако, YML и XML',
    content: (
      <>
        <p>
          Чтобы не грузить файл руками, в правиле выберите автоматический источник (поле «Источник
          данных») и задайте <b>расписание</b>:
        </p>
        <ul>
          <li><b>Файл по ссылке (xlsx/csv)</b> — вставьте прямую ссылку на файл. Поддерживаются
            Dropbox, Google Drive, Яндекс.Диск (ссылки приводятся к прямой загрузке) и онлайн‑таблицы
            (HTML). Нажмите «Загрузить поля из файла», чтобы выбрать колонки.
            <br />
            <b>Если поставщик отдаёт прайс только после входа</b> (при открытии ссылки браузер
            показывает окошко с логином и паролем) — заполните «Логин на сайте поставщика» и
            «Пароль» прямо в правиле. Приложение будет подставлять их при каждом скачивании,
            в том числе по расписанию. Пароль хранится в зашифрованном виде и обратно не
            показывается; заменить его можно, вписав новый, удалить — кнопкой «Удалить
            сохранённый пароль». Если у поставщика можно завести отдельную учётную запись под
            выгрузку — лучше указать её, а не основной доступ в личный кабинет.</li>
          <li><b>Облачная папка (Я.Диск / Google Drive)</b> — публичная папка «по ссылке». В «Как
            узнавать файл» укажите часть имени; если подходящих файлов несколько — берётся самый
            свежий. Если ссылка ведёт на <b>один файл</b> или в папке лежит ровно один файл,
            поле «Как узнавать файл» можно оставить пустым. Нажмите «Найти файл и загрузить
            поля».</li>
          <li><b>YML по ссылке</b> — XML‑фид. Нажмите «Загрузить поля из YML» и выберите свойства для
            цены и остатков.</li>
          <li><b>XML по ссылке (supplier_catalog)</b> — фид с товарами в разделе
            <code>supplier → offers → offer</code>. Нажмите «Загрузить поля из XML», затем выберите
            поле сопоставления, цены и остатка. Перед запуском проверьте «Предпросмотр».</li>
          <li><b>Файл из почты</b> — приложение само берёт вложение из письма поставщика (см. раздел
            «Настройка почты»).</li>
        </ul>
        <p>
          Для каждого источника проверьте результат кнопкой <b>«Предпросмотр»</b>, затем сохраните —
          дальше приложение будет забирать прайс по расписанию само.
        </p>
      </>
    ),
  },
  {
    id: 'mail',
    title: 'Настройка почты (автоприём прайсов)',
    content: (
      <>
        <p>
          Один почтовый ящик на компанию. Приложение периодически проверяет письма и, если домен
          отправителя привязан к поставщику, само берёт вложение (.xlsx/.xls/.csv) и создаёт приход.
        </p>
        <ul>
          <li>Вкладка <b>«Почта»</b>: включите <b>«Включить автозабор из почты»</b> и заполните
            <b> IMAP‑сервер</b> (например, <code>imap.yandex.ru</code>, <code>imap.mail.ru</code>),
            <b> порт</b> (обычно 993), <b>логин (e‑mail)</b>, <b>пароль</b> и <b>шифрование</b>
            (TLS/SSL). Пароль хранится зашифрованно.</li>
          <li>Если в почте включена двухфакторная защита (вход по коду из СМС/приложения) — обычный
            пароль сюда <b>не подойдёт</b>, нужен отдельный <b>«пароль приложения»</b>. Его создают в
            настройках вашей почты, например:
            <ul>
              <li><b>Яндекс.Почта:</b> «Настройки» → «Безопасность» → «Пароли приложений» → создать для
                «Почты (IMAP)». Скопируйте выданный пароль и вставьте в поле «Пароль».</li>
              <li><b>Mail.ru:</b> «Настройки» → «Безопасность» → «Пароли для внешних приложений» →
                создать → вставьте сюда.</li>
              <li><b>Gmail:</b> в аккаунте Google → «Безопасность» → «Пароли приложений».</li>
            </ul>
            Обычный пароль от почты вводить не нужно.</li>
          <li>Нажмите <b>«Проверить подключение»</b> — приложение покажет, что вход удался и сколько
            писем в папке. Затем <b>«Сохранить»</b>.</li>
          <li>В <b>каждом правиле</b> поставщика, который присылает прайс на почту: источник
            <b> «Файл (загрузка/почта)»</b> → <b>«Автоматически из почты»</b> → впишите <b>домены
            отправителя</b> (часть после <code>@</code>, например <code>vinaspain.ru</code>).</li>
        </ul>
        <p>
          Письма берутся по дате прихода (не по «прочитано»), каждое обрабатывается один раз, один и
          тот же файл дважды не загружается. Если письма не забираются — проверьте подключение (кнопкой),
          пароль приложения и правильность доменов в правилах.
        </p>
        <p>
          <b>Проверить почту сейчас.</b> Не ждать расписания: кнопка на вкладке «Почта» забирает
          письма немедленно и отвечает, что нашла — сколько писем проверено, сколько файлов
          загружено. Если ничего не подошло, она покажет, письма от каких отправителей вообще есть
          в ящике: по этому списку сразу видно, присылал поставщик прайс или нет.
        </p>
        <p>
          <b>«Взять последний прайс поставщика».</b> Каждое письмо обрабатывается один раз, поэтому
          пришедший ранее прайс повторно не берётся. При настройке правила это мешает: нужное письмо
          уже «пройдено», а ждать нового можно неделю. Поставьте этот флажок и выберите период
          (неделя, месяц, 3 месяца или полгода) — приложение найдёт <b>самое свежее</b> письмо
          каждого поставщика за срок и разберёт его текущими настройками. Если писем несколько,
          берётся только последнее: прайс — это снимок, а не накопление. Повторно загрузить тот же
          файл нельзя, приход не задвоится.
        </p>
      </>
    ),
  },
  {
    id: 'schedule',
    title: 'Расписание',
    content: (
      <p>
        Для автоматических источников (ссылка, папка, YML, XML) и для почты выберите, когда запускать:
        <b> по интервалу</b> (каждые N минут) или <b>раз в сутки</b> в заданное время по Москве
        (например, 03:45 ночью, когда нагрузка ниже) — утром остатки и цены уже обновлены. Разным
        правилам можно задать разное время, чтобы развести нагрузку. Важно: правило должно быть
        <b> включено</b> (тумблер на вкладке «Поставщики»), иначе по расписанию оно не сработает.
      </p>
    ),
  },
  {
    id: 'multi',
    title: 'Штучно и коробками из одного файла',
    content: (
      <p>
        Если один файл подходит сразу под несколько правил, приложение создаст несколько приходов.
        Частый случай — товар заведён и <b>штучно</b>, и <b>коробками</b>. Тогда одно правило считает
        штуки (цена ÷ кол‑во в коробке, остаток × кол‑во), другое — коробки, а во втором включается
        <b> «Добавить «‑кол‑во» в конец ключа»</b>, чтобы попасть в коробочную карточку (например,
        <code> ART</code> → <code>ART‑6</code>).
      </p>
    ),
  },
  {
    id: 'suppliers-list',
    title: 'Список поставщиков и группы',
    content: (
      <>
        <p>Вкладка <b>«Поставщики»</b> — все ваши правила. Здесь удобно навести порядок:</p>
        <ul>
          <li><b>Тумблер вкл/выкл</b> в строке правила — включить или временно отключить поставщика.
            Есть мастер‑тумблер на всю группу.</li>
          <li><b>Перетаскивание</b> — расставьте правила в удобном порядке, в том числе между группами.</li>
          <li><b>Группы</b> (кнопка «Создать группу») — объединяйте поставщиков в блоки со счётчиком
            «X/Y вкл»; свёрнутость групп запоминается.</li>
          <li><b>Экспорт / Импорт</b> — сохранить все правила в файл и восстановить (см. «Бэкап»).</li>
        </ul>
      </>
    ),
  },
  {
    id: 'reports',
    title: 'Отчёты',
    content: (
      <>
        <p>
          Вкладка <b>«Отчёты»</b> — история всех загрузок: файл, поставщик, метод, статус, сколько
          загружено / не найдено / пропущено. Нажмите на строку — детальный отчёт с причиной по каждой
          строке и кнопкой <b>«Скачать не найденные в Excel»</b>.
        </p>
        <p>
          Статусы строк: <b>Загружено</b>, <b>Не найдено</b>, <b>Нет артикула/кода</b>, <b>Нет цены</b>,
          <b> Нулевой остаток</b>, <b>Исключён правилом</b>. По ним легко понять, что пошло не так, и
          поправить правило.
        </p>
      </>
    ),
  },
  {
    id: 'prices-compare',
    title: 'Сравнение цен поставщиков',
    content: (
      <p>
        Вкладка <b>«Цены»</b>: один и тот же товар (по ключу сопоставления — артикулу или коду) у разных
        поставщиков, видно, у кого закупка дешевле — самая выгодная подсвечена. Есть фильтр «только
        товары с выбором (2+ поставщика)» и выгрузка в Excel. Данные берутся из последних успешных
        приходов каждого поставщика — удобно решать, где закупать.
      </p>
    ),
  },
  {
    id: 'stats',
    title: 'Статистика',
    content: (
      <p>
        Вкладка <b>«Статистика»</b>: сверху сводка (активных правил, прогонов сегодня/за неделю,
        обновлено позиций, что требует внимания), ниже — по каждому поставщику кривая суммы приходов в
        рублях. Помогает ловить аномалии: резкий скачок вверх — крупный новый завоз. При наведении на
        точку — сумма и дата.
      </p>
    ),
  },
  {
    id: 'backup',
    title: 'Бэкап и перенос настроек',
    content: (
      <p>
        На странице «Поставщики» кнопки <b>«Экспорт»</b> (все правила в один файл) и <b>«Импорт»</b>
        (создать правила из такого файла — они добавляются к текущим). Полезно как резервная копия,
        при переустановке или переносе на другой аккаунт. На том же МойСклад‑аккаунте привязки
        контрагентов и складов остаются рабочими.
      </p>
    ),
  },
  {
    id: 'trial',
    title: 'Пробный период, удаление и поддержка',
    content: (
      <>
        <p>
          Начните с пробного периода: установите приложение, настройте одного‑двух поставщиков, сделайте
          предпросмотр и пару загрузок. Ничего не сломается — предпросмотр и режим «Черновик» дают всё
          проверить до изменения учёта, а повторная загрузка не задваивает остатки.
        </p>
        <p>
          Если что‑то не получается — не удаляйте приложение, напишите нам: раздел <b>«Обратная связь»</b>
          в приложении, <b>чат поддержки в Telegram</b> (<a href={supportTelegram}>@sup_eion</a>) или почта{' '}
          <b>{supportEmail}</b>. Опишите, что хотите сделать или что не выходит — поможем настроить. При
          переустановке на тот же аккаунт правила и данные сохраняются.
        </p>
      </>
    ),
  },
];

export const helpFaq: HelpFaqItem[] = [
  {
    question: 'Что это за приложение и зачем оно мне?',
    answer: <p>Это помощник для МойСклад. Поставщики присылают вам прайсы (списки товаров с ценами и остатками), а вы обычно вручную переносите это в МойСклад — оприходование, цены, остатки. «Умный импорт» делает это за вас: берёт прайс, находит ваши товары и всё обновляет сам. Меньше рутины и ошибок.</p>,
  },
  {
    question: 'Я не программист, разберусь?',
    answer: <p>Да. Настройка — мышкой, по шагам, без кода. Один раз настраиваете «правило» для каждого поставщика (какой файл, какие колонки — артикул, цена, остаток), дальше приложение работает само. Есть пошаговая инструкция, а самый простой путь — нажать «Подгрузить поля из файла» и выбирать колонки из списка. Не получится — напишите нам, поможем.</p>,
  },
  {
    question: 'С чего начать после установки?',
    answer: <p>Откройте вкладку «Поставщики» → «Новый поставщик». Сразу нажмите «Подгрузить поля из файла» и выберите прайс — тогда колонки можно выбирать из списка. Дальше по инструкции: название, контрагент, организация, склад, колонка артикула/кода, колонка цены, колонка остатка → «Сохранить» → «Предпросмотр» → «Создать приходы».</p>,
  },
  {
    question: 'Не понимаю, какую колонку выбрать в настройке.',
    answer: <p>Нажмите «Подгрузить поля из файла» и выберите прайс поставщика — приложение прочитает названия колонок, и вы будете выбирать их из выпадающих списков, а не вписывать вручную. Останется только сопоставить: где артикул/код, где цена, где остаток.</p>,
  },
  {
    question: 'Что такое «контрагент», «организация», «склад»?',
    answer: <p>Это справочники вашего МойСклад. «Контрагент (поставщик)» — от кого товар (подставится в документ и в поле «Поставщик» карточки). «Организация» — на кого оформляем оприходование. «Склад» — куда попадут остатки. Всё выбирается из списков — просто укажите нужное.</p>,
  },
  {
    question: 'Искать «по Артикулу» или «по Коду» — что выбрать?',
    answer: <p>То поле, которое у ваших товаров в МойСклад реально заполнено и совпадает со значениями из прайса. Если у вас заполнен Артикул — выбирайте Артикул; если Код — Код. Не уверены — попробуйте один вариант, посмотрите предпросмотр (сколько нашлось), при необходимости переключите.</p>,
  },
  {
    question: 'Поставщик даёт в прайсе штрихкоды, а не артикулы. Подойдёт?',
    answer: <p>Да. В «Искать в МойСклад по» выберите <b>«Штрихкоду»</b>, а в «Колонке в файле» укажите колонку со штрихкодом. Сравнение идёт со штатным полем «Штрихкоды» карточки товара в МойСклад (EAN-13, EAN-8, Code128, GTIN, UPC) — тем самым, что вы заполняете в разделе «Штрихкоды». Если у товара их несколько, он найдётся по любому. Важно: если вы завели <b>собственное доп. поле</b> со штрихкодом, оно к этому варианту не относится — для него выберите «Текстовому доп. полю».</p>,
  },
  {
    question: 'У разных поставщиков разные артикулы на один и тот же товар — как быть?',
    answer: <p>Для этого есть третий вариант в «Искать в МойСклад по» — <b>«Текстовому доп. полю»</b>. В карточке товара заведите доп. поля (тип «Текст»), например «Артикул поставщика А», «Артикул поставщика Б», и впишите туда артикул каждого поставщика. Затем в правиле поставщика выберите сопоставление по его полю. Так один ваш товар находится по разным «поставщицким» артикулам. Важно: значения в доп. поля вносятся в МойСклад вручную по каждому товару (приложение их не заполняет), и поле должно быть типа «Текст» — числовые/справочники в списке не появятся.</p>,
  },
  {
    question: 'Мне нужны только остатки, себестоимость трогать не надо — можно?',
    answer: <p>Да. В разделе «Себестоимость (закупочная цена)» есть переключатель «Импортировать себестоимость (закупочную цену) из прайса» — <b>выключите его</b>. Тогда приложение обновит только остатки, а оприходование пройдёт по текущей закупочной цене карточки: себестоимость в МойСклад останется как есть, ничего не изменится. По умолчанию переключатель включён — у тех, кто импортирует себестоимость, всё работает как раньше.</p>,
  },
  {
    question: 'Мне нужны только цены, остатки менять не надо — можно?',
    answer: <p>Да. В разделе «Остатки» включите <b>«Не учитывать наличие»</b>. Приложение не потребует колонку остатка, не создаст оприходование и не изменит остатки. Оно обновит только те цены, которые включены выше: закупочную цену и/или выбранный тип цены продажи.</p>,
  },
  {
    question: 'Поставщик даёт разную скидку на разные бренды — как это настроить?',
    answer: <p>В разделе «Себестоимость (закупочная цена)» под формулой добавьте <b>правила цены</b>: условие по карточке товара в МойСклад (бренд, название, поставщик) и своя формула для этих товаров. Например: «Бренд равно Luminarc → вычесть 25%», следующим правилом «Бренд равно Rona → вычесть 15%». Всё, что не попало ни в одно правило, считается базовой формулой — её и оставьте под общую скидку. Срабатывает <b>первое подходящее правило сверху вниз</b>, поэтому частные условия ставьте выше общих. В предпросмотре видно, сколько товаров посчитало каждое правило: ноль напротив правила означает, что условие ни с чем не совпало — обычно это опечатка в названии бренда.</p>,
  },
  {
    question: 'Приложение находит мало товаров («не найдено»).',
    answer: <p>Скачайте не найденные (кнопка в отчёте) и сравните «Артикул из прайса» с реальным артикулом/кодом в МойСклад. Обычно причина: выбрано не то поле (Артикул вместо Кода), или артикул чуть отличается — тогда уберите лишний префикс/суффикс или добавьте недостающий. Подбирайте в предпросмотре. Если товаров правда нет в МойСклад — заведите карточки и загрузите снова.</p>,
  },
  {
    question: 'А если товара из прайса нет у меня в МойСклад?',
    answer: <p>Такой товар просто не попадёт в приход — каталог не засорится. Все «не найденные» видны в отчёте, их можно скачать в Excel и завести карточки в МойСклад.</p>,
  },
  {
    question: 'Оно само поменяет мои цены и остатки? Я ничего не испорчу?',
    answer: <p>Меняет только то, что вы настроили (закупочную цену, по желанию цену продажи, остаток) и только у найденных товаров. Сначала всегда есть «Предпросмотр» (ничего не создаёт) и режим «Черновик» (документ непроведённый — вы проверяете и проводите сами). Без вашего действия ничего «в бой» не уйдёт.</p>,
  },
  {
    question: 'Можно сначала посмотреть, что получится, не записывая?',
    answer: <p>Да — кнопка «Предпросмотр» показывает «будет загружено / не найдено / пропущено» и список ненайденных, ничего не создавая. Убедились — «Создать приходы».</p>,
  },
  {
    question: 'Загрузил, но остатки в МойСклад не изменились. Почему?',
    answer: <p>Чаще всего оприходование создалось как «Черновик» (в правиле выбран режим «Черновик») — черновик не влияет на остатки, пока вы не проведёте его в МойСклад вручную. Либо документ создан на другой склад — проверьте склад в правиле. Откройте оприходование в МойСклад и посмотрите его статус.</p>,
  },
  {
    question: 'Не задвоятся ли остатки, если загружать каждый день?',
    answer: <p>Нет. Новый приход создаётся на текущую дату, а предыдущий приход этого поставщика автоматически снимается (или удаляется — на выбор). Документы, заведённые вручную, приложение не трогает.</p>,
  },
  {
    question: 'Цена или остаток записались неправильно — как исправить?',
    answer: <p>Откройте деталь отчёта: там видна цена и количество по каждому товару. Обычно причина — выбрана не та колонка (например, «резерв» вместо «остаток», или скидка вместо цены) или лишняя формула. Проверьте, какие колонки выбраны, и уберите/поправьте формулу. Для коробок укажите «кол‑во в коробке» и добавьте «× на кол‑во в коробке».</p>,
  },
  {
    question: 'Поставщик даёт цены/остатки за коробку, а я продаю поштучно?',
    answer: <p>Укажите поле «Колонка «кол‑во в коробке»» — приложение вытащит число из текста (КОР6 → 6). Затем в формуле остатка добавьте «× на кол‑во в коробке», а в формуле цены — «÷ на кол‑во в коробке». Можно даже из одного файла завести и штучный, и коробочный товар.</p>,
  },
  {
    question: 'Поставщик пишет остаток словами («много», «мало») или сроком («10 дней»). Что делать?',
    answer: <p>В разделе «Остатки» добавьте <b>правило остатка</b>: слева условие («содержит», «равно», «начинается с»…), в середине — само слово, справа — какой остаток записать. Например «содержит <b>дней</b> → 5» или «равно <b>много</b> → 50». Правила проверяются сверху вниз, срабатывает первое подходящее. Важно: без такого правила текст «10 дней» будет прочитан как число 10 — то есть срок поставки уедет в остаток.</p>,
  },
  {
    question: 'Как не показывать реальный остаток поставщика (чтобы не выкупили всё)?',
    answer: <p>Тем же механизмом — «Правила остатка», но с числовым условием. «Больше 100 → записать 10» скроет реальную глубину склада, «меньше 20 → записать 3» убережёт от продажи того, что заканчивается. Можно задать несколько условий подряд, сработает первое подходящее. Если ни одно не подошло — остаток берётся из файла как обычно.</p>,
  },
  {
    question: 'Настроил почту, но ждать следующей проверки долго. Можно запустить сразу?',
    answer: <p>Да. Вкладка «Почта» → кнопка <b>«Проверить почту сейчас»</b> — приложение сразу заберёт новые письма, не дожидаясь расписания. Результат появится во вкладке «Отчёты». Кнопка работает и когда автоматический приём выключен: это разовый забор, регулярный опрос она не включает.</p>,
  },
  {
    question: 'У меня несколько складов / несколько колонок остатков.',
    answer: <p>В «Колонки остатков» добавьте нужные колонки — они сложатся в общий остаток. Или возьмите только один склад — как вам нужно.</p>,
  },
  {
    question: 'Один артикул есть у двух поставщиков — как не дать чужому прайсу перезаписать цену?',
    answer: <p>В правиле <b>чужого</b> поставщика откройте блок <b>«Исключения товаров по каталогу МойСклад»</b> и добавьте условие по карточке — например «Бренд равно Pulltex». Приложение найдёт товар у вас в каталоге, увидит этот бренд и <b>полностью пропустит</b> его: не создаст приход, не изменит закупочную цену, цену продажи и поставщика. Ваши данные в карточке останутся нетронутыми. Условие можно ставить и по флажку в карточке, и по названию товара.</p>,
  },
  {
    question: 'Чем «исключения по файлу поставщика» отличаются от «исключений по каталогу МойСклад»?',
    answer: <p>Первые смотрят в <b>колонку прайса</b> — например, колонка «Категория» содержит «SALE». Вторые смотрят в <b>вашу карточку товара в МойСклад</b> — бренд, флажок, название. Второй вариант нужен тогда, когда в прайсе поставщика нужных данных просто нет: бренд он не присылает, а вы его у себя проставили. Работают независимо, можно использовать оба сразу.</p>,
  },
  {
    question: 'Можно не загружать часть товаров (например, акции)?',
    answer: <p>Да — «Исключения товаров»: колонка + условие (содержит / маска / регулярное выражение), например «Категория» содержит «SALE». Совпавшие не приходуются. А лишний файл в папке (например, акционный) можно пропускать по слову в имени.</p>,
  },
  {
    question: 'Как сделать, чтобы прайсы грузились сами?',
    answer: <p>Выберите в правиле автоматический источник — из почты, по ссылке, из облачной папки, YML или XML supplier_catalog — и задайте расписание (каждые N минут или раз в сутки ночью). Дальше приложение забирает прайс и обновляет цены/остатки само; вам останется иногда заглядывать в «Отчёты».</p>,
  },
  {
    question: 'Нажал «Проверить почту сейчас», а ничего не произошло. Почему?',
    answer: <p>Приложение обрабатывает каждое письмо <b>один раз</b>: если прайс уже приходил и был обработан (или пришёл до того, как вы настроили правило), повторно он не берётся. Поставьте флажок <b>«Взять последний прайс поставщика»</b> и выберите период — приложение найдёт самое свежее письмо от каждого поставщика за этот срок и разберёт его. Сама кнопка теперь отвечает, что нашла: сколько писем проверено и от каких отправителей — по этому списку сразу видно, присылал ли поставщик прайс.</p>,
  },
  {
    question: 'Поставщик присылает прайс раз в месяц. Найдёт ли приложение старое письмо?',
    answer: <p>Да. В флажке «Взять последний прайс поставщика» выберите период — вплоть до полугода. Приложение просматривает ящик вглубь и берёт самое свежее письмо каждого поставщика за выбранный срок. Если писем от него за период не было, вы увидите список отправителей, чьи письма нашлись: значит, нужно либо увеличить период, либо проверить домен отправителя в правиле.</p>,
  },
  {
    question: 'Настроил почту, но письма не забираются.',
    answer: <p>Проверьте на вкладке «Почта» кнопкой «Проверить подключение» (покажет ошибку или число писем). Частые причины: не введён пароль приложения (при двухфакторной защите), неверный сервер/порт, не включён автозабор. И проверьте, что в правиле поставщика указан правильный «домен отправителя» (часть после @).</p>,
  },
  {
    question: 'Настроил расписание, но по нему ничего не грузится.',
    answer: <p>Убедитесь, что правило включено (зелёный тумблер на вкладке «Поставщики») — выключенное по расписанию не работает. Проверьте время (оно по Москве) и что ссылка/папка доступна. Загляните в «Отчёты» — были ли недавние прогоны этого правила.</p>,
  },
  {
    question: 'Поставщик присылает прайс в Excel — подойдёт?',
    answer: <p>Да. Поддерживаются Excel (.xlsx, .xls, .xlsm), .csv, онлайн-таблицы, YML и XML структуры supplier_catalog. «Кривой» табличный прайс (шапка не на первой строке, лишние строки, цены с пробелами вроде «1 200», двухэтажная шапка) приложение понимает.</p>,
  },
  {
    question: 'Что если поставщик изменит формат своего файла?',
    answer: <p>Приложение проверяет файл до записи. Если ожидаемые колонки не совпали — предупредит и не создаст «кривой» документ, пока вы не поправите правило (например, заново выберете колонки).</p>,
  },
  {
    question: 'Загрузил файл вручную, а пишет «правило не найдено». Что делать?',
    answer: <p>Ничего страшного — просто имя файла не совпало с полем «Как узнавать файл» ни в одном правиле (а при ручной загрузке оно и не обязано совпадать). Под файлом появится список <b>«Выберите правило для этого файла вручную»</b> — укажите, по какому правилу обработать этот файл, и нажмите «Предпросмотр». Если хотите, чтобы такие файлы определялись сами, впишите в нужном правиле часть имени файла в поле «Как узнавать файл» (для ручной загрузки это поле необязательное).</p>,
  },
  {
    question: 'Это безопасно? Вы видите мои товары и цены?',
    answer: <p>Приложение работает внутри вашего МойСклад по официальному доступу. Любое изменение можно сначала посмотреть в предпросмотре и откатить (новый приход снять/удалить). Доступ к почте, если подключаете, хранится в зашифрованном виде. Ссылки на прайсы проверяются, доступ аккаунтов изолирован.</p>,
  },
  {
    question: 'Как понять, что загрузка прошла и что именно изменилось?',
    answer: <p>После каждого запуска есть отчёт: сколько загружено, что не найдено и что пропущено (и почему), сколько карточек обновлено по закупке/продаже/поставщику. Не найденные можно выгрузить в Excel. А на вкладке «Статистика» видна динамика приходов по каждому поставщику.</p>,
  },
  {
    question: 'Где посмотреть, у какого поставщика выгоднее?',
    answer: <p>На вкладке «Цены»: один и тот же товар у разных поставщиков, видно, у кого закупка дешевле (с выгрузкой в Excel). Удобно для решения, где закупать.</p>,
  },
  {
    question: 'Что делают «Экспорт» и «Импорт» в списке поставщиков?',
    answer: <p>Это резервная копия и перенос всех правил. «Экспорт» скачивает все правила одним файлом (храните как бэкап), «Импорт» создаёт правила из такого файла (добавляются к текущим). Удобно перед переустановкой или при переносе на другой аккаунт. На том же аккаунте привязки контрагентов и складов остаются рабочими.</p>,
  },
  {
    question: 'Переустановил приложение — настройки не потеряются?',
    answer: <p>Нет. Правила и данные сохраняются и возвращаются при повторной установке на тот же аккаунт. А на всякий случай есть «Экспорт» всех правил в файл и «Импорт» обратно.</p>,
  },
  {
    question: 'Боюсь настроить неправильно и что-то сломать в учёте.',
    answer: <p>Ничего не сломается без вашего действия. Используйте «Предпросмотр» (ничего не создаёт) и режим «Черновик» (документ непроведённый — сами проверяете и проводите). Повторная загрузка не задваивает остатки, ручные документы приложение не трогает. А если сомневаетесь — напишите нам, поможем настроить, не удаляйте приложение.</p>,
  },
  {
    question: 'Куда писать, если что-то непонятно или не работает?',
    answer: <p>Через раздел «Обратная связь» в приложении, в чат поддержки в Telegram (<a href={supportTelegram}>@sup_eion</a>) или на почту {supportEmail}. Опишите, что хотите сделать или что сломалось — поможем и подскажем.</p>,
  },
];

export const helpChangelog: HelpChangelogEntry[] = [
  {
    date: '2 ноября 2026',
    title: 'Импорт XML-фидов supplier_catalog по ссылке',
    body: (
      <p>
        В правиле появился отдельный источник <b>«XML по ссылке (supplier_catalog)»</b>.
        Приложение читает товары из структуры <code>supplier → offers → offer</code> и предлагает
        поля фида для сопоставления, цены и остатка. Вставьте ссылку, нажмите
        «Загрузить поля из XML» и проверьте результат через «Предпросмотр» перед импортом.
        Другие структуры XML пока не поддерживаются.
      </p>
    ),
  },
  {
    date: '28 сентября 2026',
    title: 'Обновление только цен — без остатков и оприходования',
    body: (
      <p>
        В каждом правиле появился переключатель <b>«Не учитывать наличие»</b>. Включите его,
        если прайс нужен только для цен: приложение не читает и не меняет остатки, не создаёт
        оприходование и обновляет только выбранные вами закупочную цену и/или цену продажи.
      </p>
    ),
  },
  {
    date: '26 сентября 2026',
    title: 'Личные сообщения от разработчиков',
    body: (
      <p>
        Теперь мы можем написать вам лично — например, если увидим, что у вашего правила
        перестала открываться ссылка поставщика или импорт неделю не проходит. Такие
        сообщения приходят сюда же, во вкладку «Обновления», но выделены цветом и помечены
        <b> «Только для вас»</b>: это написано вашему аккаунту, а не всем сразу. Количество
        непрочитанных видно на самой вкладке. Отвечать можно через «Обратную связь» —
        кнопка в правом верхнем углу.
      </p>
    ),
  },
  {
    date: '26 сентября 2026',
    title: 'Приложение само предупреждает, если правило перестало работать',
    body: (
      <p>
        Раньше сломавшееся правило можно было заметить только в «Отчётах» — а туда заходят,
        уже что-то заподозрив. Из-за этого правила молча не загружались неделями: у одного
        поставщика сменилась ссылка, и прайс не приходил 60 раз подряд. Теперь на вкладке
        <b> «Поставщики»</b>, прямо у названия правила, появляется предупреждение:
        «Не загружается 5 раз подряд» и причина. Единичные сбои не показываем — у поставщиков
        бывает, что сайт моргнул; предупреждение появляется с третьего отказа подряд и
        краснеет, когда их больше десяти. Как только загрузка пройдёт успешно, оно исчезает.
      </p>
    ),
  },
  {
    date: '26 сентября 2026',
    title: 'Понятные причины отказа вместо технических строк',
    body: (
      <>
        <p>Две частые ошибки в отчётах перестали быть загадкой:</p>
        <ul>
          <li>
            Вместо <code>Invalid HTML: could not find &lt;table&gt;</code> приложение теперь
            пишет, что по ссылке пришла <b>веб-страница, а не файл с прайсом</b> — так бывает,
            когда ссылка ведёт на страницу входа или на страницу с кнопкой «скачать». И
            подсказывает, что делать: запросить прямую ссылку на файл, а если выгрузка
            закрыта входом — указать логин и пароль в правиле.
          </li>
          <li>
            Когда отказ приходит от <b>МойСклада</b>, в отчёте теперь его собственная причина
            («Объект с таким именем уже существует» и т. п.), а не кусок служебного запроса.
            Раньше при массовом обновлении карточек в сообщение попадало начало нашего же
            запроса, по которому понять ничего было нельзя.
          </li>
        </ul>
      </>
    ),
  },
  {
    date: '25 сентября 2026',
    title: 'Понятная ошибка при подключении почты',
    body: (
      <p>
        Gmail, Яндекс.Почта и Mail.ru не пускают сторонние программы по обычному паролю от
        ящика — нужен отдельный <b>«пароль приложения»</b>. Раньше при отказе входа приложение
        советовало «проверьте логин и пароль», и человек вводил тот же пароль снова. Теперь
        сообщение прямо говорит, что нужен пароль приложения, и подсказывает, где его создать
        именно в вашей почте — а если такого раздела нет, что сначала нужно включить
        двухэтапную аутентификацию. Технический хвост вроде «Invalid credentials (Failure)»
        из сообщения убран.
      </p>
    ),
  },
  {
    date: '17 сентября 2026',
    title: 'Комплект в каталоге больше не срывает весь импорт',
    body: (
      <p>
        Если позиция прайса совпадала с <b>комплектом</b> (или услугой) в МойСкладе, МойСклад
        отклонял всё оприходование целиком — комплекты оприходовать нельзя. Из-за одной
        такой позиции не загружались тысячи остальных, и так на каждом запуске. Теперь
        такая позиция пропускается, а остальные загружаются как обычно. В отчёте о загрузке
        она видна среди пропущенных с причиной «это комплект — комплекты нельзя
        оприходовать». Если комплект сопоставился по ошибке, проверьте в его карточке
        поле, по которому идёт сопоставление.
      </p>
    ),
  },
  {
    date: '14 сентября 2026',
    title: 'Импорт по расписанию стал заметно быстрее',
    body: (
      <>
        <p>
          Три изменения, которые видно на больших прайсах:
        </p>
        <ul>
          <li>
            <b>Закупочная цена и поставщик</b> теперь записываются в карточку одним
            обращением к МойСкладу вместо двух — раньше один и тот же список товаров
            уходил туда дважды подряд.
          </li>
          <li>
            <b>Карточки, где всё уже верно, не переписываются.</b> Если в МойСкладе
            стоят ровно та же закупочная цена и тот же поставщик, приложение их не
            трогает. В отчёте такие позиции показаны отдельно — «Не потребовалось
            менять», чтобы «обновлено 0» не выглядело сбоем.
          </li>
          <li>
            <b>Данные карточек перед записью читаются заново.</b> Раньше приложение
            могло работать по снимку каталога до часа давности — и правка, сделанная
            в МойСкладе только что (например, заполненный «Поставщик»), учитывалась
            не сразу. Теперь перед каждым импортом карточки перечитываются, так что
            ваши свежие изменения видны немедленно.
          </li>
          <li>
            <b>Разные аккаунты больше не стоят друг за другом.</b> Раньше загрузки
            шли одной общей очередью, и тяжёлый прайс у одного пользователя задерживал
            расписание у остальных. Теперь аккаунты обрабатываются параллельно, а
            правила внутри одного аккаунта — по-прежнему по очереди.
          </li>
        </ul>
      </>
    ),
  },
  {
    date: '14 сентября 2026',
    title: 'Прайс с сайта, где нужен логин и пароль',
    body: (
      <p>
        Некоторые поставщики отдают выгрузку только после входа: при открытии ссылки браузер
        показывает окошко с логином и паролем, а приложение получало отказ и не могло скачать
        файл. Теперь в правиле с источником «Файл по ссылке» есть поля <b>«Логин на сайте
        поставщика»</b> и <b>«Пароль»</b> — приложение подставляет их при каждом скачивании,
        включая запуски по расписанию. Пароль хранится в зашифрованном виде и обратно не
        показывается. Если при скачивании поставщик перенаправит нас на другой сайт, логин и
        пароль туда не передаются.
      </p>
    ),
  },
  {
    date: '14 сентября 2026',
    title: 'Облачная папка: «Как узнавать файл» больше не обязательно',
    body: (
      <p>
        Если ссылка ведёт на <b>один файл</b> (Яндекс.Диск <code>/i/…</code>, Google Drive
        <code>/file/d/…</code>, Google Таблица) или в папке лежит <b>ровно один</b> файл —
        поле «Как узнавать файл» можно оставить пустым: брать всё равно нечего, кроме него.
        Раньше приложение требовало вписать слово из имени и отвечало невнятным «Файл по
        условию «» не найден». Если файлов в папке несколько, приложение теперь так и пишет —
        сколько их и как они называются, чтобы было понятно, что вписать.
      </p>
    ),
  },
  {
    date: '14 сентября 2026',
    title: 'Ссылки Яндекс 360 на Диск теперь работают',
    body: (
      <p>
        Публичные ссылки вида <code>disk.360.yandex.ru</code> — это Яндекс.Диск в корпоративном
        Яндекс 360 — приложение не опознавало как Диск и пыталось скачать по ним обычную
        веб-страницу вместо файла. Загрузка падала с ошибкой разбора. Теперь такие ссылки
        работают наравне с <code>disk.yandex.ru</code> и <code>yadi.sk</code>: и как «Файл по
        ссылке», и как «Облачная папка». Настройки менять не нужно — просто повторите загрузку.
      </p>
    ),
  },
  {
    date: '10 сентября 2026',
    title: 'Google Таблицы как источник прайса и починка «только остатков»',
    body: (
      <>
        <p>
          <b>Ссылки на Google Таблицы теперь работают.</b> Раньше приложение понимало только файлы
          и папки Google Диска, а на ссылку вида <code>docs.google.com/spreadsheets/…</code>
          отвечало «не распознана ссылка». Теперь такую таблицу можно указать в источнике{' '}
          <b>«Файл по ссылке»</b> — приложение скачает её само. Таблица должна быть открыта
          «по ссылке».
        </p>
        <p>
          Если ссылку на таблицу вставить в поле <b>облачной папки</b>, приложение больше не
          отвечает загадочной ошибкой, а прямо говорит, какой источник выбрать.
        </p>
        <p>
          <b>Исправлено: «Укажите колонку закупочной цены» при выключенном импорте
          себестоимости.</b> Правило, которому нужны только остатки, невозможно было ни сохранить,
          ни выполнить — приложение требовало колонку цены, которой в прайсе может не быть вовсе.
          Теперь колонки цены спрашиваются, только если себестоимость действительно импортируется.
        </p>
      </>
    ),
  },
  {
    date: '10 сентября 2026',
    title: 'Заработали облачные папки Google Drive',
    body: (
      <>
        <p>
          Источник <b>«Облачная папка»</b> для Google Drive раньше отвечал ошибкой «Google Drive не
          настроен». Причина была на нашей стороне — доступ к Google Drive не был подключён. Теперь
          подключён: приложение читает папку и само находит в ней нужный файл, как это давно
          работает с Яндекс.Диском.
        </p>
        <p>
          Важное условие, оно же теперь написано прямо в настройке правила: <b>папка должна быть
          открыта «по ссылке»</b> — доступной каждому, у кого есть адрес. Если поставщик открывает
          папку приглашением на конкретную почту, приложение её не прочитает: оно заходит без
          учётной записи. Это ограничение Google, обойти его нельзя — попросите у поставщика
          обычную ссылку.
        </p>
      </>
    ),
  },
  {
    date: '1 сентября 2026',
    title: 'Видно, насколько свежие данные из МойСклада — и кнопка обновить их',
    body: (
      <>
        <p>
          Условия по карточке (бренд, поставщик, название) приложение проверяет не по живому
          запросу в МойСклад, а по <b>копии каталога</b>, которую снимает раз в час: ходить за
          каждой карточкой на десятках тысяч строк прайса невозможно.
        </p>
        <p>
          Побочный эффект был неприятным: поправили поставщика в карточке — а приложение ещё до
          часа считает по-старому и молча пропускает товар. Понять причину со стороны было нельзя.
        </p>
        <p>
          Теперь в предпросмотре видно, <b>на какой момент взяты данные карточек</b>, а рядом —
          ссылка <b>«обновить из МойСклад»</b>. Нажали, загрузили файл ещё раз — и приложение
          считает по свежим карточкам, ждать час не нужно.
        </p>
      </>
    ),
  },
  {
    date: '21 августа 2026',
    title: 'Разные скидки на разные товары в закупочной цене',
    body: (
      <>
        <p>
          Поставщик часто присылает одну цену, а скидку даёт разную: на один бренд 25%, на другой
          15%, на остальное 35%. Раньше это приходилось разводить руками или отдельными правилами.
        </p>
        <p>
          Теперь в разделе <b>«Себестоимость (закупочная цена)»</b> под формулой можно добавить{' '}
          <b>правила для отдельных групп товаров</b>. Каждое правило — это условие по{' '}
          <b>карточке товара в МойСклад</b> (бренд, название, поставщик) и своя формула. Условий в
          одном правиле может быть несколько, тогда они должны совпасть все сразу: «бренд Luminarc{' '}
          <b>и</b> название содержит „декантер“».
        </p>
        <p>
          Базовая формула остаётся главной и считает всё, что не попало ни в одно правило.{' '}
          <b>Срабатывает первое подходящее правило сверху вниз</b> — частные условия ставьте выше
          общих, порядок меняется стрелками. Можно и наоборот: выбрать, чтобы товары без
          подходящего правила <b>вообще не обрабатывались</b>.
        </p>
        <p>
          В предпросмотре видно, <b>сколько товаров посчитало каждое правило</b>. Если напротив
          правила ноль — значит условие не совпало ни с одним товаром, скорее всего опечатка в
          названии бренда. Ошибку видно сразу, до создания оприходования.
        </p>
        <p>
          Если правил не задавать, всё работает ровно как раньше — одна формула на весь прайс.
          Заодно из блока убрано <b>округление</b>: МойСклад хранит закупочную цену в копейках, и
          приложение всегда приводило её к копейкам, так что выбор ни на что не влиял.
        </p>
      </>
    ),
  },
  {
    date: '20 августа 2026',
    title: 'Больше времени на скачивание и на большие прогоны',
    body: (
      <>
        <p>
          Многие поставщики формируют прайс в момент запроса по ссылке, и на крупных файлах
          минуты не хватало — загрузка обрывалась, хотя файл был в порядке. Теперь на скачивание
          отводится <b>3 минуты</b> вместо одной.
        </p>
        <p>
          Заодно подняли предел для самих прогонов: раньше загрузка, идущая дольше{' '}
          <b>30 минут</b>, останавливалась как зависшая. На прайсах в сотни тысяч строк это
          срабатывало на живой работе, у которой оставалось совсем немного до конца. Теперь предел —{' '}
          <b>90 минут</b>.
        </p>
        <p>
          На обычных прайсах ничего не меняется: они загружаются за секунды и в прежние границы
          укладывались с большим запасом.
        </p>
      </>
    ),
  },
  {
    date: '20 августа 2026',
    title: 'Понятно, когда файл не отдал сайт поставщика',
    body: (
      <>
        <p>
          Если прайс загружается по ссылке или из облака, файл иногда не приходит: сервер
          поставщика не успел его сформировать, ключ в ссылке перестал действовать, у поставщика
          авария. Раньше в отчёте появлялась техническая строка вроде «This operation was
          aborted» — по ней невозможно было понять ни что случилось, ни к кому идти.
        </p>
        <p>
          Теперь приложение пишет это словами: <b>что произошло</b>, <b>на чьей стороне</b> сбой и{' '}
          <b>что делать</b>. Например: «Сайт поставщика не прислал файл за 60 секунд. Так бывает,
          когда прайс формируется в момент запроса и сервер не успевает его отдать. Задержка на его
          стороне, не в приложении — попробуйте запустить загрузку ещё раз».
        </p>
        <p>
          Отдельно разбираются устаревшая ссылка, закрытый доступ, отсутствующий файл, ограничение
          по частоте обращений и авария на стороне поставщика. Там, где виновата настройка самого
          правила, приложение так и скажет — и не станет валить на поставщика.
        </p>
      </>
    ),
  },
  {
    date: '19 августа 2026',
    title: 'Сервер стал вдвое мощнее — сбои при обработке файлов устранены',
    body: (
      <>
        <p>
          Возможно, на этой неделе вы замечали: приложение отвечало с задержкой, а загрузка прайса
          могла оборваться без внятного объяснения. Извините — рассказываем, что это было и что мы
          сделали.
        </p>
        <p>
          Приложением пользуется всё больше компаний, и прайсы у всех разные: у кого-то пара сотен
          позиций, а у кого-то — больше <b>150 000 строк</b> в одном файле. На таких файлах мы упёрлись
          в возможности сервера: ему не хватало памяти, и это задевало не только владельца большого
          прайса, но и всех остальных — у них запросы обрывались.
        </p>
        <p>
          Что сделали: <b>увеличили сервер вдвое</b> — вдвое больше оперативной памяти и вдвое больше
          вычислительных ядер. Второе ядро важно не меньше памяти: раньше обработка одного тяжёлого
          файла занимала сервер целиком, и остальные ждали в очереди.
        </p>
        <p>
          Что это даёт вам: приложение работает <b>быстрее и стабильнее</b>, большие прайсы теперь
          обрабатываются целиком, а чужая тяжёлая загрузка больше не мешает вашей работе. Проверено
          на файле в 153 660 строк — раньше он не проходил вовсе, теперь разбирается за 39 секунд.
        </p>
      </>
    ),
  },
  {
    date: '19 августа 2026',
    title: 'Новое в линейке: RoiSklad',
    body: (
      <p>
        В блоке «Другие наши решения» появился <b>RoiSklad</b> — сервис аналитики склада: показывает
        неликвид, считает план закупа в рублях, строит ABC×XYZ и GMROI по данным вашего МоегоСклада.
        Отвечает на вопрос «сколько денег заморожено на складе и что с этим делать». Подробнее —{' '}
        <a href="https://roisklad.e-ion.ru">roisklad.e-ion.ru</a>. Это отдельный сервис, не
        приложение маркетплейса, поэтому ссылка ведёт на сайт. На «Умный импорт» не влияет.
      </p>
    ),
  },
  {
    date: '19 августа 2026',
    title: 'Исключения по каталогу МойСклад: добавился поставщик',
    body: (
      <p>
        В блок <b>«Исключения товаров по каталогу МойСклад»</b> добавлено условие по{' '}
        <b>поставщику</b> из карточки товара — рядом с брендом, флажком и названием. Теперь можно
        сказать «не трогать товары, у которых в карточке указан другой поставщик»: чужой прайс не
        перезапишет цены и остатки по вашим товарам. Поставщик выбирается из списка ваших
        контрагентов, сравнение — «равно» или «не равно».
      </p>
    ),
  },
  {
    date: '17 августа 2026',
    title: 'Исключения по каталогу МойСклад — защита своих цен от чужого прайса',
    body: (
      <>
        <p>
          В правило добавлен блок <b>«Исключения товаров по каталогу МойСклад»</b>. В отличие от
          прежних исключений, он не читает файл поставщика: приложение находит товар из прайса
          <b> у вас в каталоге</b> и смотрит его <b>карточку</b> — бренд, флажок, название. Если
          карточка подходит под условие, товар <b>полностью пропускается</b>: не приходуется, не
          меняются закупочная цена, цена продажи и поставщик.
        </p>
        <p>
          Зачем: один и тот же артикул часто есть у нескольких поставщиков, и чужой прайс
          перезаписывал ваши данные. Условие «Бренд равно …» в правиле чужого поставщика это решает.
          Поле выбирается из списка ваших доп. полей МойСклад. Прежний блок исключений переименован
          в <b>«по файлу поставщика»</b> — чтобы не путать эти два вида.
        </p>
        <p>
          Если условий не задано, всё работает ровно как раньше. На скорость загрузки это не влияет.
        </p>
      </>
    ),
  },
  {
    date: '17 августа 2026',
    title: 'Детализация отчётов хранится 10 дней',
    body: (
      <p>
        Построчная детализация прогонов (какой товар, по какой цене и в каком количестве) теперь
        хранится <b>10 дней</b> вместо 30. Сами отчёты, суммы приходов и статистика остаются в
        полном объёме — сокращается только «расшифровка» старых прогонов. Это заметно разгружает
        хранилище у тех, кто загружает большие прайсы по расписанию, и ускоряет работу приложения.
      </p>
    ),
  },
  {
    date: '13 августа 2026',
    title: 'Проверка почты вручную: поиск последнего прайса и понятный ответ',
    body: (
      <>
        <p>
          На вкладке <b>«Почта»</b> кнопка <b>«Проверить почту сейчас»</b> теперь <b>отвечает</b>,
          что именно произошло: сколько писем проверено, сколько файлов загружено. А если ничего не
          подошло — покажет, письма от каких отправителей есть в ящике, и сразу видно, присылал
          поставщик прайс или нет. Раньше кнопка молчала, и было непонятно, работает она вообще.
        </p>
        <p>
          Рядом появился флажок <b>«Взять последний прайс поставщика»</b> с выбором периода — неделя,
          месяц, 3 месяца или полгода. Он нужен при настройке: каждое письмо обрабатывается один раз,
          поэтому уже пришедший прайс повторно не берётся, а ждать нового можно неделю. С флажком
          приложение найдёт <b>самое свежее</b> письмо каждого поставщика за выбранный срок. Если
          писем несколько, возьмётся только последнее: прайс — это снимок, а не накопление. Повторно
          загрузить тот же файл нельзя, приход не задвоится.
        </p>
      </>
    ),
  },
  {
    date: '13 августа 2026',
    title: 'Тяжёлые прайсы с картинками открываются быстро',
    body: (
      <p>
        Прайсы со встроенными картинками — обычно это выгрузки из 1С на несколько мегабайт —
        обрабатывались очень долго, а вкладка при выборе такого файла могла «замирать». Теперь
        картинки пропускаются (для импорта они не нужны), а образец разбирает сервер. На реальном
        файле в 9,7 МБ это <b>полторы секунды вместо 25–30</b>. Заодно увеличен запас памяти:
        раньше очень большой прайс мог оборвать обработку.
      </p>
    ),
  },
  {
    date: '13 августа 2026',
    title: 'Исправлено: импорт останавливался из-за дробной закупочной цены',
    body: (
      <p>
        Если в карточке товара закупочная цена хранилась с дробной частью (так бывает при пересчёте
        валют или цене за единицу измерения), обновление каталога прерывалось с ошибкой и импорт не
        доходил до конца. Причина устранена, такие цены корректно округляются. Отдельных действий не
        требуется.
      </p>
    ),
  },
  {
    date: '11 августа 2026',
    title: 'Правила остатка: слова и сроки вместо числа, ограничение глубины склада',
    body: (
      <>
        <p>
          В разделе «Остатки» появились <b>правила остатка</b>. Первое, что они решают: поставщик
          пишет в колонке остатка не число, а <b>слово</b> («много», «мало», «под заказ») или{' '}
          <b>срок поставки</b> («10 дней», «3 дня»). Теперь можно задать: «содержит <b>дней</b> →
          записать 5», «равно <b>много</b> → записать 50».
        </p>
        <p>
          ⚠️ Это важно проверить, если ваши поставщики пишут сроки: без правила текст «10 дней»
          читался как число <b>10</b>, и срок поставки попадал в остаток.
        </p>
        <p>
          Вторая задача — обратная: <b>не показывать реальную глубину склада</b>. Условие «больше 100
          → записать 10» скроет, сколько у поставщика на самом деле, а «меньше 20 → записать 3»
          убережёт от продажи того, что вот‑вот закончится. Правила проверяются сверху вниз,
          срабатывает первое подходящее; вместо числа можно выбрать «как есть» или «пропустить
          товар». Если правил нет — всё работает ровно как раньше.
        </p>
      </>
    ),
  },
  {
    date: '11 августа 2026',
    title: 'Кнопка «Проверить почту сейчас»',
    body: (
      <p>
        Раньше после настройки почты приходилось ждать расписание — а это может быть 30–60 минут.
        Теперь на вкладке <b>«Почта»</b> есть кнопка <b>«Проверить почту сейчас»</b>: приложение
        сразу заберёт новые письма и обработает вложения, результат появится в «Отчётах». Кнопка
        работает и при выключенном автоматическом приёме — это разовый забор, регулярный опрос она
        не включает и расписание не сбивает.
      </p>
    ),
  },
  {
    date: '10 августа 2026',
    title: 'Приложение точнее находит шапку в «многоэтажных» прайсах',
    body: (
      <>
        <p>
          В прайсах из 1С шапка часто раскидана по нескольким строкам: например, «Артикул»,
          «Штрихкод» и «Номенклатура» подписаны в одной строке, а «Цена» и{' '}
          <b>«Свободный остаток»</b> — на три строки ниже. Раньше приложение брало верхнюю строку, и
          колонки из нижнего яруса <b>вообще не появлялись в списке</b> — выбрать остатки было
          невозможно.
        </p>
        <p>
          Теперь при загрузке образца приложение подбирает строку заголовков так, чтобы{' '}
          <b>подписи получили как можно больше колонок</b>, независимо от того, на каком ярусе шапки
          они стоят. Если у вас такой прайс — откройте правило, нажмите «Подгрузить поля из файла»
          ещё раз, и недостающие колонки появятся в списках. Правила, которые уже работают, менять не
          нужно: сохранённая строка заголовков остаётся прежней.
        </p>
      </>
    ),
  },
  {
    date: '3 августа 2026',
    title: 'Сопоставление товаров по штрихкоду',
    body: (
      <>
        <p>
          В «Искать в МойСклад по» добавился вариант <b>«Штрихкоду»</b> — четвёртый способ находить
          ваши товары, наряду с артикулом, кодом и текстовым доп. полем. Пригодится, когда поставщик
          присылает в прайсе штрихкоды, а артикулы у вас с ним не совпадают: штрихкод — общий язык.
        </p>
        <p>
          Сравнение идёт со <b>штатным полем «Штрихкоды»</b> карточки товара — тем, что вы
          заполняете в МойСклад в одноимённом разделе (EAN-13, EAN-8, Code128, GTIN, UPC). Если у
          товара несколько штрихкодов, он найдётся <b>по любому</b> из них. Если же вы завели своё
          доп. поле со штрихкодом — для него по-прежнему используйте «Текстовому доп. полю».
        </p>
      </>
    ),
  },
  {
    date: '28 июля 2026',
    title: 'Новое в линейке: «Умные акции маркетплейсов»',
    body: (
      <p>
        В блоке «Другие наши решения» появился пятый продукт —{' '}
        <b>«Умные акции маркетплейсов»</b>. Он следит, чтобы ваши товары попадали в акции
        маркетплейсов <b>только тогда, когда скидка укладывается в ваш порог</b>: не нужно вручную
        проверять каждую акцию и считать, не уйдёте ли вы в минус. Подробнее —{' '}
        <a href="https://profit.e-ion.ru">profit.e-ion.ru</a>. На «Умный импорт» это никак не
        влияет, продукты независимы.
      </p>
    ),
  },
  {
    date: '26 июля 2026',
    title: 'Аудит безопасности: данные аккаунтов проверены',
    body: (
      <>
        <p>
          Мы провели полный аудит приложения по методике для решений МойСклад — отдельно проверяли
          главное: <b>может ли один аккаунт увидеть данные другого</b>. Проверены все обращения к
          базе, авторизация виджета, хранение токенов доступа и фоновые задачи.{' '}
          <b>Утечек между аккаунтами не обнаружено</b>: данные каждой компании изолированы, токены
          хранятся в зашифрованном виде и никогда не передаются в браузер.
        </p>
        <p>
          Заодно усилили защиту: добавили заголовки безопасности (приложение теперь может
          открываться только внутри МоегоСклада и нигде больше) и настроили, чтобы служебные
          журналы не разрастались. Для вас ничего не меняется — всё работает как прежде.
        </p>
      </>
    ),
  },
  {
    date: '26 июля 2026',
    title: 'Исправлено: в Chrome не работали удаление правил и импорт',
    body: (
      <p>
        Если вы работаете в <b>Chrome</b>, кнопки <b>«Удалить правило»</b>, <b>«Удалить группу»</b> и{' '}
        <b>импорт правил из файла</b> могли не срабатывать: нажимаете — и ничего не происходит, без
        всякой ошибки. Причина — Chrome блокирует стандартные окна подтверждения внутри приложений,
        встроенных в МойСклад (в Safari они работали, поэтому проблема была заметна не всем).
        Теперь вместо системного окна — <b>наше собственное окно подтверждения</b>, оно работает во
        всех браузерах.
      </p>
    ),
  },
  {
    date: '24 июля 2026',
    title: 'Чат поддержки в Telegram',
    body: (
      <p>
        Нужен быстрый ответ — теперь есть <b>чат поддержки в Telegram</b>:{' '}
        <a href="https://t.me/sup_eion">@sup_eion</a>. Ссылка появилась в форме «Обратная связь», в
        разделе поддержки и на сайте. Почта <b>support@e-ion.ru</b> и форма обратной связи в
        приложении работают как раньше — просто теперь можно выбрать, что удобнее.
      </p>
    ),
  },
  {
    date: '21 июля 2026',
    title: 'Исправлено: прогон завершался ошибкой, хотя приход создавался',
    body: (
      <p>
        У правил с настройкой <b>«Удалять предыдущий приход»</b> загрузка могла заканчиваться
        статусом <b>«Ошибка»</b>, хотя оприходование в МойСклад на самом деле создавалось. Сбой
        происходил на следующем шаге — при снятии прошлого прихода поставщика, поэтому старые
        документы оставались и остатки могли задвоиться. Причина найдена и устранена. Отдельно
        делать ничего не нужно: при следующей загрузке такого поставщика накопившиеся прошлые
        приходы снимутся автоматически, останется один актуальный.
      </p>
    ),
  },
  {
    date: '21 июля 2026',
    title: 'Ручная загрузка: выбор правила и «Как узнавать файл» — по желанию',
    body: (
      <>
        <p>
          Загружаете файл вручную, а его имя не совпало ни с одним правилом — раньше приложение писало
          «правило не найдено» и на этом всё. Теперь под таким файлом появляется <b>выпадающий список
          «Выберите правило для этого файла вручную»</b>: указали, по какому правилу обработать, — и
          загрузка идёт. Ручная загрузка больше не привязана жёстко к имени файла.
        </p>
        <p>
          Поле <b>«Как узнавать файл»</b> в правиле стало <b>необязательным</b>. Заполняйте его, только
          если хотите, чтобы приложение <b>само</b> определяло правило по части имени файла (это нужно
          для автоприёма из почты, по ссылке и из облака, где файлов может быть несколько). Для ручной
          загрузки его можно оставить пустым и выбирать правило вручную.
        </p>
      </>
    ),
  },
  {
    date: '21 июля 2026',
    title: '«Статистика» открывается быстро при любой истории',
    body: (
      <p>
        Вкладка <b>«Статистика»</b> с ростом истории открывалась всё медленнее — приложение каждый раз
        пересчитывало суммы по всем прошлым приходам. Теперь исторические суммы <b>фиксируются один раз</b>
        и не пересчитываются, обновляется только текущий день. Страница открывается быстро независимо от
        того, сколько у вас накопилось приходов. Цифры и графики те же.
      </p>
    ),
  },
  {
    date: '21 июля 2026',
    title: 'Настройка сопоставления проще: обязательное отдельно от необязательного',
    body: (
      <p>
        Раздел <b>«Сопоставление товара»</b> в правиле разделили на две части. Сверху — только то, что
        нужно для поиска ваших товаров (<b>по чему искать</b> и <b>колонка артикула/кода</b>). А тонкая
        настройка — обрезка префиксов/суффиксов артикула и разбор коробок — убрана в отдельный
        сворачиваемый блок <b>«Подгонка артикула и коробки (необязательно)»</b>. Он свёрнут по
        умолчанию: новичку не мешает лишнее, а кому нужно — раскрыл и настроил. Все поля и их работа
        прежние, просто разложены понятнее.
      </p>
    ),
  },
  {
    date: '18 июля 2026',
    title: 'Сопоставление по текстовому доп. полю',
    body: (
      <p>
        <b>Разные артикулы у разных поставщиков — теперь не проблема.</b> В «Искать в МойСклад по»
        добавился вариант <b>«Текстовому доп. полю»</b>. Заведите в карточке товара доп. поля («Артикул
        поставщика А», «Артикул поставщика Б»…), впишите артикул каждого поставщика — и в правиле
        матчите по нужному полю. Один ваш товар находится по разным «поставщицким» артикулам. Поле
        должно быть типа «Текст», значения вносятся в МойСклад вручную по каждому товару.
      </p>
    ),
  },
  {
    date: '18 июля 2026',
    title: 'Импорт «только остатки» — себестоимость по желанию',
    body: (
      <p>
        В разделе «Себестоимость» появился переключатель <b>«Импортировать себестоимость (закупочную
        цену) из прайса»</b>. Выключите его, если от поставщика нужны <b>только остатки</b> —
        оприходование пройдёт по текущей закупочной цене карточки, себестоимость в МойСклад останется
        как есть. По умолчанию включён — у всех, кто импортировал себестоимость, ничего не меняется.
      </p>
    ),
  },
  {
    date: '9 июля 2026',
    title: 'Переехали на отдельный сервер — стабильнее и быстрее',
    body: (
      <>
        <p>
          Приложением стало пользоваться больше компаний, и в часы пиковой нагрузки случались
          <b> кратковременные сбои и замедления</b> — иногда виджет открывался не сразу. Мы разобрались
          с причиной и <b>перенесли «Умный импорт» на отдельный выделенный сервер</b>, независимый от
          других наших сервисов.
        </p>
        <p>
          Что это даёт вам: приложение работает <b>стабильнее и быстрее</b>, а возможные перебои у
          соседних сервисов больше на него не влияют — ресурсы теперь только для «Умного импорта».
          Все ваши <b>правила, настройки, подключения и история сохранены</b> — с вашей стороны делать
          ничего не нужно.
        </p>
      </>
    ),
  },
  {
    date: '7 июля 2026',
    title: 'Подробная пошаговая инструкция и обновлённый FAQ',
    body: (
      <>
        <p>
          Инструкция переписана как путь от установки до первой загрузки: установка → настройка первого
          поставщика по шагам → предпросмотр → отчёт, а дальше — все тонкости (сопоставление, цены,
          остатки, коробки, исключения, автоматизация, почта, расписание). Простым языком, с примерами,
          названия кнопок и полей — как в приложении.
        </p>
        <p>
          Раздел «Вопрос‑Ответ» дополнен частыми вопросами при настройке: почему «не найдено» и как
          исправить, почему остатки не изменились (черновик), почта не забирает письма, расписание не
          сработало, коробки → штуки и другие.
        </p>
      </>
    ),
  },
  {
    date: '3 июля 2026',
    title: 'Поставщик в карточке — ползунок на правило',
    body: (
      <>
        <p>
          Поле «Поставщик» в карточке товара теперь при импорте всегда ставится равным контрагенту
          правила — тому, чья цена записалась в карточку. Так поставщик и его цена в карточке всегда
          согласованы. Если поставщик уже верный, история товара не засоряется.
        </p>
        <p>
          В настройках каждого правила добавлен ползунок <b>«Обновлять поставщика в карточке товара»</b>
          (по умолчанию включён) — можно отключить для отдельных поставщиков. А в списке поставщиков
          свёрнутые группы теперь остаются свёрнутыми при переходах между вкладками.
        </p>
      </>
    ),
  },
  {
    date: '11 июня 2026',
    title: 'Группы поставщиков — перетаскиванием',
    body: (
      <p>
        В списке поставщиков появилась кнопка «Создать группу». Создаёте группу и перетаскиваете в
        неё нужные правила мышкой — даже в пустую. Группу можно включать/выключать одним тумблером и
        удалять (правила тогда станут «без группы»). Поле «Группа» из карточки правила убрано — теперь
        всё делается прямо в списке.
      </p>
    ),
  },
  {
    date: '11 июня 2026',
    title: 'Стабильность на больших прайсах',
    body: (
      <p>
        Очень большие прайсы (десятки тысяч строк) теперь обрабатываются надёжно. Импорт запускается
        в фоне: приложение сразу принимает задачу и открывает «Отчёты», где статус меняется с «В
        обработке» на «Готово». Раньше на таких объёмах загрузка шла дольше, чем браузер держит
        соединение, и выглядела как ошибка, хотя приход успевал создаться.
      </p>
    ),
  },
  {
    date: '11 июня 2026',
    title: 'Справка и обратная связь в приложении',
    body: (
      <p>
        В приложении появились разделы «Вопрос-Ответ» (частые вопросы простым языком) и
        «Обновления», а также форма «Обратная связь» — вопрос или сообщение о проблеме уходит нам на
        почту прямо из приложения.
      </p>
    ),
  },
  {
    title: 'Статистика по поставщикам',
    body: (
      <>
        <p>
          Появилась вкладка <b>«Статистика»</b>: по каждому поставщику строится кривая суммы
          оприходований в рублях — за последние 100 приходов. Это видно, как меняется ваш объём
          закупки у конкретного поставщика во времени.
        </p>
        <p>
          Главная польза — ловить аномалии. Резкий скачок вверх сразу заметен: это крупный новый
          завоз. При наведении на точку показываются точная сумма прихода и его дата.
        </p>
      </>
    ),
  },
  {
    title: 'Сравнение закупочных цен — «у кого дешевле»',
    body: (
      <>
        <p>
          Новая вкладка <b>«Цены»</b>: один и тот же товар (по ключу сопоставления — артикулу или
          коду) сопоставляется у всех поставщиков, у которых он есть, и видно, у кого закупка
          выгоднее — самая низкая цена подсвечена.
        </p>
        <p>
          Есть фильтр «только товары с выбором (2+ поставщика)» — чтобы сразу видеть, где реально есть
          из чего выбирать, — и кнопка выгрузки сравнения в Excel. Данные берутся из последних
          успешных приходов каждого поставщика.
        </p>
      </>
    ),
  },
  {
    title: 'Сводка, группы и порядок, бэкап настроек',
    body: (
      <>
        <p>
          На вкладке «Статистика» появилась <b>сводка</b>: сколько правил активно, прогонов
          сегодня и за неделю, сколько позиций обновлено, и что требует внимания. Контроль «всё ли
          работает» одним взглядом.
        </p>
        <p>
          Список поставщиков переделан: компактные строки с перетаскиванием (свой порядок),
          включение/выключение тумблером, объединение в <b>группы</b> с мастер‑тумблером на группу, и
          подпись с методом и расписанием.
        </p>
        <p>
          Добавлены <b>Экспорт</b> и <b>Импорт</b> всех правил — выгрузка в файл и восстановление
          одним кликом. Удобно для бэкапа и переноса на другой аккаунт; настройки не теряются даже
          при переустановке приложения.
        </p>
      </>
    ),
  },
  {
    title: 'Автоприём прайсов из почты',
    body: (
      <>
        <p>
          Поставщик присылает прайс на почту — приложение само забирает вложение и создаёт
          оприходование. Письмо привязывается к поставщику по <b>домену отправителя</b>, открывать и
          скачивать ничего не нужно.
        </p>
        <p>
          Один почтовый ящик на компанию (IMAP, пароль хранится зашифрованно, есть «Проверить
          подключение»). Письма берутся по дате прихода, каждое обрабатывается один раз. Можно
          включить режим «Черновик», чтобы проверять документы перед проведением.
        </p>
      </>
    ),
  },
  {
    title: 'Любой источник прайса: ссылка, облако, YML',
    body: (
      <>
        <p>
          Кроме ручной загрузки файла, прайс можно забирать по <b>прямой ссылке</b> (в т.ч. Dropbox,
          Google Drive, Яндекс.Диск — ссылки приводятся к прямой загрузке), из <b>облачной папки</b>
          (Я.Диск / Google Drive — нужный файл ищется по части имени, при нескольких берётся самый
          свежий) и из <b>YML‑каталога</b> по ссылке.
        </p>
        <p>
          Поддерживаются и онлайн‑таблицы на странице (HTML), в том числе в кодировке windows‑1251 —
          такие «таблицы остатков» теперь читаются без «кракозябр».
        </p>
      </>
    ),
  },
  {
    title: 'Запуск по расписанию',
    body: (
      <p>
        Источники по ссылке/папке/YML и почту можно проверять <b>по интервалу</b> (каждые N минут)
        или <b>раз в сутки</b> в заданное время по Москве — например, ночью, когда нагрузка ниже.
        Разным правилам можно задать разное время, чтобы развести нагрузку. Настроили один раз —
        дальше обновляется само.
      </p>
    ),
  },
  {
    title: 'Несколько приходов из одного файла (штучно + коробками)',
    body: (
      <>
        <p>
          Если один файл подходит сразу под несколько правил поставщика, создаётся несколько приходов.
          Это закрывает частый случай, когда товар заведён и <b>штучно</b>, и <b>коробками</b> —
          в разные карточки.
        </p>
        <p>
          В формулах появились операции «× / ÷ на количество в коробке»: для штучной карточки цена
          делится, а остаток умножается на вложение; для коробочной — к ключу добавляется суффикс
          «‑количество», чтобы попасть в нужную карточку. Остатки нескольких складов суммируются в один.
        </p>
      </>
    ),
  },
  {
    title: 'Исключения и подготовка ключа',
    body: (
      <>
        <p>
          Можно не приходовать часть позиций по условию (колонка + «содержит» / маска / регулярное
          выражение, несколько правил по «ИЛИ»), а в папке — пропускать ненужные файлы по слову в
          имени (например, <code>SALE</code>), чтобы из дневного и акционного прайса брался только нужный.
        </p>
        <p>
          Артикул/код перед поиском можно подготовить: добавить префикс/суффикс, убрать лишние символы
          (например, пробелы: <code>120 586</code> → <code>120586</code>) или подстроку — чтобы точно
          попасть в карточку МойСклад. В колонках цены и остатка значения вида <code>&gt;50</code>
          приводятся к числу, а «двухэтажные» шапки (заголовок строкой выше) распознаются автоматически.
        </p>
      </>
    ),
  },
  {
    title: 'Предпросмотр, понятные отчёты и защита остатков',
    body: (
      <>
        <p>
          Перед записью — <b>предпросмотр</b>: приложение показывает «будет загружено / не найдено /
          пропущено» и список ненайденных, ничего не создавая. Плюс режим «Черновик» — документ
          создаётся непроведённым, вы проверяете и проводите сами.
        </p>
        <p>
          По каждому прогону — отчёт с причинами пропусков (нет ключа/цены, нулевой остаток,
          исключено) и кнопкой «Скачать не найденные в Excel» — удобно заводить новые карточки.
          Повторная загрузка не задваивает остатки: новый приход на текущую дату, предыдущий
          (созданный приложением) снимается с проведения. Ваши ручные документы приложение не трогает.
        </p>
      </>
    ),
  },
  {
    title: 'Запуск приложения в МойСклад',
    body: (
      <>
        <p>
          Приложение установлено и работает на боевых данных. Базовая механика: по имени файла
          распознаёт поставщика и применяет его правило, находит товары по артикулу или коду, считает
          цены по вашей формуле и обновляет закупочную цену, цену продажи и остатки в карточках.
        </p>
        <p>
          Без всякой настройки пропускает строки без ключа, без цены и с нулевым остатком — всё видно
          в отчёте. Настройка правила — пошагово, мышкой по образцу файла, без кода.
        </p>
      </>
    ),
  },
];
